import styled, { css } from 'styled-components';

import { getCDNImageUrl } from '@/helpers/cdn';

import { device, hexToRGB } from '@/styles/helpers';

const grayHeartIcon = getCDNImageUrl('/greyHeartIcon.webp');
const heart = getCDNImageUrl('/emojis/heart.webp');
const fullScreenIcon = getCDNImageUrl('/fullScreenIcon.svg');

export interface ScButtonCloseProps {
  top?: number;
  right?: number;
  dark?: boolean;
  gray?: boolean;
  inCircle?: boolean;
  small?: boolean;
  isStatic?: boolean;
}

export const ScButtonClose = styled.button<ScButtonCloseProps>`
  display: block;
  position: ${({ isStatic }) => (isStatic ? 'static' : 'absolute')};
  border: none;
  top: ${({ top }) => `${top}px`};
  right: ${({ right }) => `${right}px`};
  background-color: transparent;
  width: ${({ small }) => (small ? '32px' : '40px')};
  height: ${({ small }) => (small ? '32px' : '40px')};
  min-width: fit-content;
  cursor: pointer;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 40px;
    height: 40px;
    object-fit: contain;
    transition: transform 0.3s;

    path {
      fill: ${({ theme, dark, gray }) =>
        dark
          ? theme.blackColor
          : gray
            ? theme.blackLightColor
            : theme.whiteColor};
    }
  }

  @media (hover: hover) {
    &:hover {
      svg {
        transform: rotate(90deg);
      }
    }
  }
`;

export interface ScButtonMoreProps {
  top?: number;
  right?: number;
  active?: boolean;
  vertical?: boolean;
}

export const ScButtonMore = styled.button<ScButtonMoreProps>`
  display: block;
  border: none;
  background-color: transparent;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 1;
  transform: ${({ active, vertical }) =>
    `rotate(${active && !vertical ? 0 : 90}deg)`};
  transition: 0.3s;

  &:hover {
    transform: ${({ active, vertical }) =>
      `rotate(${active && !vertical ? 90 : 0}deg)`};
  }

  & > div {
    border-radius: 50%;
    display: inline-block;
    vertical-align: middle;
    width: 4px;
    height: 4px;
    background: #ffffff;
    margin: 0 2px;
  }
`;

export interface ScButtonDetailsProps {
  top?: number;
  right?: number;
}

export const ScButtonDetails = styled.button<ScButtonDetailsProps>`
  display: block;
  position: absolute;
  border: none;
  top: ${({ top }) => `${top}px`};
  right: ${({ right }) => `${right}px`};
  background-color: transparent;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 1;
  transition: 0.3s;
  & > div {
    display: inline-block;
    vertical-align: middle;
    width: 4px;
    height: 4px;
    background: ${({ theme }) => theme.grayMegaLightColor};
    margin: 0 2px;
    transition: 0.3s;
  }
  &:hover {
    & > div {
      background: #8e8e93;
    }
  }
`;

export interface ScButtonHeartProps {
  top?: number;
  right?: number;
  active?: boolean;
  backgroundColor?: string;
  width?: number;
  height?: number;
}

export const ScButtonHeart = styled.button<ScButtonHeartProps>`
  display: block;
  position: absolute;
  border: none;
  z-index: 4;
  top: ${({ top }) => `${top}px`};
  right: ${({ right }) => `${right}px`};
  background-color: ${({ backgroundColor }) =>
    backgroundColor || 'transparent'};

  background-image: ${({ active }) =>
    active ? `url(${heart})` : `url(${grayHeartIcon})`};

  background-position: center;
  background-size: 98%;
  background-repeat: no-repeat;
  width: ${({ width }) => (width != null ? `${width}px` : '40px')};
  height: ${({ height }) => (height != null ? `${height}px` : '40px')};
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    transform: scale(0.8);
  }
`;

export const ScButtonHeartLoader = styled.div<ScButtonHeartProps>`
  -webkit-animation: pulsate 1s linear infinite;
  -moz-animation: pulsate 1s linear infinite;
  animation: pulsate 1s ease-out infinite;
  background-image: ${({ active }) =>
    active ? `url(${heart})` : `url(${grayHeartIcon})`};
  background-position: center;
  background-size: 98%;
  background-repeat: no-repeat;
  position: absolute;
  z-index: 3;
  top: ${({ top }) => `${top}px`};
  right: ${({ right }) => `${right}px`};
  width: ${({ width }) => (width != null ? `${width}px` : '40px')};
  height: ${({ height }) => (height != null ? `${height}px` : '40px')};

  @-webkit-keyframes pulsate {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.8);
    }
  }

  @-moz-keyframes pulsate {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.8);
    }
  }

  @keyframes pulsate {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.8);
    }
  }
`;

export interface ScButtonFullScreenProps {
  top?: number;
  right?: number;
}

export const ScButtonFullScreen = styled.button<ScButtonFullScreenProps>`
  display: block;
  position: absolute;
  border: none;
  top: ${({ top }) => `${top}px`};
  right: ${({ right }) => `${right}px`};
  background-color: transparent;
  background-image: url(${fullScreenIcon});
  background-position: center;
  background-repeat: no-repeat;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 1;
  transition: 0.2s;

  :hover {
    transform: scale(0.8);
  }
`;

export interface ScButtonWithArrayProps {
  active?: boolean;
  selected?: boolean;
  icon?: React.ComponentType;
}

export const ScButtonWithArray = styled.button<ScButtonWithArrayProps>`
  position: relative;
  z-index: 12;
  background-color: ${({ theme, selected }) =>
    selected ? theme.whiteColor : theme.grayLightColor};
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  height: 40px;
  width: 100%;
  max-width: 192px;
  box-sizing: border-box;
  border-radius: 28px;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme, active, selected }) =>
    selected
      ? theme.blackColor
      : active
        ? theme.whiteColor
        : theme.blackLightColor};
  transition: border-color 0.2s;
  cursor: pointer;

  .providerLogo {
    position: absolute;
    width: 40px;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    & > img {
      width: 20px;
      height: 20px;
      object-fit: contain;
      vertical-align: bottom;
    }
  }

  @media (hover: hover) {
    &:hover {
      color: ${({ theme, selected }) => !selected && theme.whiteColor};

      & > .arrow svg path {
        stroke: ${({ theme, selected }) =>
          selected ? theme.blackColor : theme.whiteColor};
      }
    }
  }

  ${({ icon }) =>
    icon != null &&
    css`
      & > .icon {
        display: none;
      }
    `}

  & > .value {
    font-size: 16px;
    line-height: 24px;
    padding: ${({ selected, icon }) =>
      selected && icon ? '0 48px 0 40px' : '0 48px 0 16px'};
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
    transition: 0.2s;
  }

  & > .arrow {
    position: absolute;
    right: 16px;
    top: 8px;
    transition: transform 0.2s;
    transform: ${({ active }) => (active ? 'rotate(180deg)' : 'rotate(0deg)')};
    svg {
      display: block;
      path {
        stroke: ${({ theme, active }) =>
          active ? theme.whiteColor : theme.grayColor};
      }
    }
  }

  @media (${device.mobile}) {
    ${({ theme, selected, icon }) =>
      icon != null &&
      css`
        height: 40px;
        & > .arrow {
          display: none;
        }
        & > .value {
          width: 100%;
        }
        & > .icon {
          display: inline-block;
          margin-right: 4px;
          svg {
            display: inline-block;
            vertical-align: middle;
            path,
            circle {
              stroke: ${selected ? theme.blackColor : theme.grayColor};
              fill: ${selected ? theme.whiteColor : theme.grayLightColor};
            }
          }
        }
      `}

    & > .icon {
      display: ${({ selected }) => selected && 'none'};
    }
  }
`;

export interface ScButtonTupleProps {
  active?: boolean;
}

export const ScButtonTuple = styled.button<ScButtonTupleProps>`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme, active }) =>
    hexToRGB(theme.whiteColor, active ? 1 : 0)};
  border: none;
  cursor: pointer;
  overflow: hidden;
  transition: 0.3s;

  .tuple {
    position: absolute;
    left: ${({ active }) => (active ? `-100%` : `0%`)};
    width: 200%;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    transition: 0.2s;

    .tuple_item {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;

      svg {
        width: 40px;
        height: 40px;
        object-fit: contain;
        path {
          fill: ${({ theme, active }) =>
            active ? theme.grayDarkColor : theme.whiteColor};
        }
      }
    }
  }

  @media (${device.mobile}) {
    width: 40px;
    height: 40px;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.grayDarkColor};

      @media (${device.mobile}) {
        background: ${({ theme, active }) =>
          hexToRGB(theme.whiteColor, active ? 1 : 0)};
      }
    }
  }
`;

export interface ScTabBarButtonProps {
  active?: boolean;
}

export const ScTabBarButton = styled.button<ScTabBarButtonProps>`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  overflow: hidden;
  transition: 0.3s;
  background: ${({ active, theme }) =>
    active ? theme.whiteColor : `transparent`};

  @media (${device.mobile}) {
    width: 40px;
    height: 40px;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.grayDarkColor};

      @media (${device.mobile}) {
        background: ${({ theme, active }) =>
          hexToRGB(theme.whiteColor, active ? 1 : 0)};
      }
    }
  }
`;

export const ScPromotionFloatButton = styled.div`
  position: fixed;
  right: calc(-164px + 54px);
  top: 50%;
  margin-top: -28px;
  width: 164px;
  height: 56px;
  background: #fff;
  padding: 0px 10px 0px 0px;
  border-radius: 60px 0 0px 60px;
  z-index: 13;
  transition: 0.2s;
  display: flex;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    right: 0;
  }
  .pin {
    background: ${({ theme }) => theme.redColor};
    border: 2px solid #ffffff;
    position: absolute;
    border-radius: 50%;
    width: 12px;
    height: 12px;
    right: 6px;
    top: 4px;
    z-index: 2;
  }
  .imgWrp {
    flex: 0 0 56px;
    max-width: 56px;
    height: 56px;
    overflow: hidden;
    position: relative;
    border-radius: 60px 60px 0px 0px;
    .layer {
      vertical-align: bottom;
      width: 56px;
      height: auto;
      bottom: 0px;
      left: 2px;
    }
  }
  .text {
    flex: 1 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;
    height: 100%;
  }
  span {
    font-weight: bold;
    font-size: 10px;
    line-height: 12px;
    color: #000;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 4px;
  }
  .link {
    font-size: 12px;
    line-height: 22px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: rgba(234, 91, 12, 1);
    padding: 0 8px;
    text-align: center;
    border: 1px solid rgba(234, 91, 12, 1);
    border-radius: 60px;
    color: #fff;
    text-decoration: none;
    transition: 0.2s;

    @media (hover: hover) {
      &:hover {
        color: rgba(234, 91, 12, 1);
        background: #fff;
      }
    }
  }
`;

export interface ScScrollTopButtonProps {
  right?: number;
  bottom?: number;
}

export const ScScrollTopButton = styled.button<ScScrollTopButtonProps>`
  z-index: 20;
  width: 40px;
  height: 40px;
  background-color: white;
  position: fixed;
  right: ${({ right }) => (right ? `${right}px` : 0)};
  bottom: 32px;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.4))
    drop-shadow(0px 0px 20px rgba(0, 0, 0, 0.4));
  border-radius: 40px;
  transition: 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  & > .arrow {
    display: block;
    z-index: 2;
    transition: all 0.3s ease 0s;
    position: relative;
    width: 24px;
    height: 24px;
    transform: rotate(-90deg);

    &:before,
    &:after {
      content: '';
      display: block;
      width: 10px;
      height: 1px;
      background-color: ${({ theme }) => theme.blackColor};
      position: absolute;
      left: 7px;
      transition: all 0.3s ease 0s;
    }

    &:before {
      top: 15px;
      transform: rotate(-50deg);
    }
    &:after {
      bottom: 15px;
      transform: rotate(50deg);
    }
  }

  &:hover {
    transform: scale(1.1);

    & > .arrow {
      &:before {
        transform: rotate(-65deg);
        top: 16px;
      }
      &:after {
        transform: rotate(65deg);
        bottom: 16px;
      }
    }
  }

  @media (${device.tablet}) {
    bottom: ${({ bottom }) => (bottom ? `${bottom + 16}px` : 0)};
    &:hover {
      & > .arrow {
        &:before {
          transform: rotate(-50deg);
          top: 15px;
        }
        &:after {
          transform: rotate(50deg);
          bottom: 15px;
        }
      }
    }
  }

  @media (${device.mobile}) {
    right: ${({ right }) => (right ? `${right}px` : 0)};
  }
`;
